import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FetchSiteDevicesQuery } from '@/lib/api/sites/devices';
import { UnmonitoredDevicesSidebar } from './DevicesTab/UnmonitoredDevicesSidebar';
import { ZonesCanvas } from './DevicesTab/ZonesCanvas';

interface DevicesTabProps {
  siteId: string;
  companyId: string;
}

export interface DeviceData {
  id: string;
  type: 'camera' | 'speaker' | 'relay';
  name: string;
  assetName?: string;
  parentName?: string;
  relatedComponentGroups?: string[];
  monitored?: boolean;
  nvrId?: string;
  nvrName?: string;
  componentGroupId?: string;
  parentCameraId?: string;
  activationWkt?: string;
  originalDevice?: any;
  associatedCameraIds?: string[];
  associatedCameraId?: string;
  enabled?: boolean;
  description?: string;
  quillNotes?: string;
  capabilities?: any;
}

// Custom hook to process device data
export const useDeviceData = (siteId: string, companyId: string) => {
  const { data: siteDevices, isLoading: devicesLoading } = useQuery(FetchSiteDevicesQuery({ companyId, projectId: siteId }));

  // Categorize devices based on integration type and extract nested components
  const { cameras, speakers, relays } = useMemo(() => {
    const cameras: DeviceData[] = [];
    const speakers: DeviceData[] = [];
    const relays: DeviceData[] = [];

    if (!siteDevices?.devices) {
      return { cameras, speakers, relays };
    }

    siteDevices.devices.forEach((device, index) => {
      if (!device.secondaryDevice) return;

      const deviceName = device.secondaryDevice.name || `Device ${index + 1}`;
      const integrationType = (device.secondaryDevice as any).integrationType || 'Nvr';
      const nvrName = (device as any).nvrName || 'Unknown NVR';

      if (integrationType === 'Speaker') {
        if (device.secondaryDevice.speakers && device.secondaryDevice.speakers.length > 0) {
          const speakerComponent = device.secondaryDevice.speakers[0];
          speakers.push({
            id: speakerComponent.id || `speaker_${index}`,
            type: 'speaker',
            name: speakerComponent.name || deviceName,
            assetName: deviceName,
            parentName: deviceName,
            monitored: device.secondaryDevice.monitored,
            nvrId: (device as any).nvrId,
            nvrName,
            relatedComponentGroups: speakerComponent.relatedComponentGroups || [],
            componentGroupId: speakerComponent.componentGroupId || device.secondaryDevice.id,
          });
        } else {
          speakers.push({
            id: device.secondaryDevice.id || `speaker_${index}`,
            type: 'speaker',
            name: deviceName,
            assetName: deviceName,
            parentName: deviceName,
            monitored: device.secondaryDevice.monitored,
            nvrId: (device as any).nvrId,
            nvrName,
            relatedComponentGroups: [],
            componentGroupId: device.secondaryDevice.id,
          });
        }
      } else if (integrationType === 'Relay') {
        if (device.secondaryDevice.relays && device.secondaryDevice.relays.length > 0) {
          const relayComponent = device.secondaryDevice.relays[0];
          relays.push({
            id: relayComponent.id || `relay_${index}`,
            type: 'relay',
            name: relayComponent.name || deviceName,
            assetName: deviceName,
            parentName: deviceName,
            monitored: device.secondaryDevice.monitored,
            nvrId: (device as any).nvrId,
            nvrName,
            relatedComponentGroups: relayComponent.relatedComponentGroups || [],
            componentGroupId: relayComponent.componentGroupId || device.secondaryDevice.id,
          });
        } else {
          relays.push({
            id: device.secondaryDevice.id || `relay_${index}`,
            type: 'relay',
            name: deviceName,
            assetName: deviceName,
            parentName: deviceName,
            monitored: device.secondaryDevice.monitored,
            nvrId: (device as any).nvrId,
            nvrName,
            relatedComponentGroups: [],
            componentGroupId: device.secondaryDevice.id,
          });
        }
      } else {
        // Default to camera for all other integration types
        cameras.push({
          id: device.secondaryDevice.id || `camera_${index}`,
          type: 'camera',
          name: deviceName,
          assetName: deviceName,
          parentName: deviceName,
          monitored: device.secondaryDevice.monitored,
          nvrId: (device as any).nvrId,
          nvrName,
          relatedComponentGroups: [],
          activationWkt: device.secondaryDevice?.activationWkt || undefined,
          originalDevice: device.secondaryDevice,
        });

        // Extract nested speakers from camera device
        if (device.secondaryDevice?.speakers) {
          device.secondaryDevice.speakers.forEach((speaker, speakerIndex) => {
            if (speaker) {
              speakers.push({
                id: speaker.id || `${device.secondaryDevice?.id}_speaker_${speakerIndex}`,
                type: 'speaker',
                name: speaker.name || `Speaker ${speakerIndex + 1}`,
                assetName: deviceName,
                parentName: deviceName,
                monitored: device.secondaryDevice?.monitored || false,
                nvrId: (device as any).nvrId,
                nvrName,
                relatedComponentGroups: speaker.relatedComponentGroups || [],
                componentGroupId: speaker.componentGroupId,
                parentCameraId: device.secondaryDevice?.id || '',
              });
            }
          });
        }

        // Extract nested relays from camera device
        if (device.secondaryDevice?.relays) {
          device.secondaryDevice.relays.forEach((relay, relayIndex) => {
            if (relay) {
              relays.push({
                id: relay.id || `${device.secondaryDevice?.id}_relay_${relayIndex}`,
                type: 'relay',
                name: relay.name || `Relay ${relayIndex + 1}`,
                assetName: deviceName,
                parentName: deviceName,
                monitored: device.secondaryDevice?.monitored || false,
                nvrId: (device as any).nvrId,
                nvrName,
                relatedComponentGroups: relay.relatedComponentGroups || [],
                componentGroupId: relay.componentGroupId,
                parentCameraId: device.secondaryDevice?.id || '',
              });
            }
          });
        }
      }
    });

    return { cameras, speakers, relays };
  }, [siteDevices?.devices]);

  // Get monitored cameras (for zones canvas)
  const monitoredCameras = cameras.filter(camera => camera.monitored);

  // Get unmonitored cameras
  const unmonitoredCameras = cameras.filter(camera => !camera.monitored);

  // Get unconnected speakers and relays (no relatedComponentGroups)
  const unconnectedSpeakers = speakers.filter(speaker => 
    !speaker.relatedComponentGroups || speaker.relatedComponentGroups.length === 0
  );
  
  const unconnectedRelays = relays.filter(relay => 
    !relay.relatedComponentGroups || relay.relatedComponentGroups.length === 0
  );

  return {
    cameras,
    speakers,
    relays,
    monitoredCameras,
    unmonitoredCameras,
    unconnectedSpeakers,
    unconnectedRelays,
    isLoading: devicesLoading,
  };
};

const DevicesTab: React.FC<DevicesTabProps> = ({ siteId, companyId }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { unmonitoredCameras, unconnectedSpeakers, unconnectedRelays } = useDeviceData(siteId, companyId);

  return (
    <div className="h-full w-full flex bg-gray-900 overflow-hidden">
      {/* Left Sidebar - Unmonitored Devices */}
      <UnmonitoredDevicesSidebar
        siteId={siteId}
        companyId={companyId}
        cameras={unmonitoredCameras}
        speakers={unconnectedSpeakers}
        relays={unconnectedRelays}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      {/* Right Pane - Zones Canvas */}
      <ZonesCanvas siteId={siteId} companyId={companyId} />
    </div>
  );
};

export default DevicesTab;